import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { first } from 'rxjs/operators';
import {environment} from 'src/environments/environment';
import { DatalayerService } from 'src/services/datalayer.service';
import { AppConstants } from 'src/utils/app.constants';
import { LocalStorage } from 'src/utils/local-storage';

@Component({
  selector: 'app-ad-container',
  templateUrl: './ad-container.component.html',
  styleUrls: ['./ad-container.component.scss']
})
export class AdContainerComponent implements OnInit, OnDestroy { 

  private readonly AD_ROTATION_INTERVAL = 30000;

  @ViewChild('adContainer', { static: true })
  public adContainer: ElementRef<HTMLElement>;

  @Input()
  public adType: string;

  public currentAdData: any;

  public isOnlyShowingDefaultAds: boolean;

  private currentLanguage: string;
  private lastShownAdCampaign: string;
  private adPostmessageListener = (event: MessageEvent) => this.receiveAdViewMessage(this, event);
  
  private removeAdClickListener: (() => void) | null = null;
  private removeVisibilityChangeListener: (() => void) | null = null;
  private rotationTimeout: ReturnType<typeof setTimeout> | null = null;
  private reviveId: string | null = null;

  constructor(public httpClient: HttpClient,
              private datalayerService: DatalayerService,
              private renderer: Renderer2,
              private localStorage: LocalStorage,
              private translateService: TranslateService
  ) {
    this.currentLanguage = this.translateService.currentLang;

    const userProfile = this.localStorage.getUserProfile();
    this.isOnlyShowingDefaultAds = !!userProfile && !!userProfile.industry
    && AppConstants.INDUSTRY_CODES_TO_HIDE_ADS
      .indexOf(userProfile.industry) > -1;

    window.addEventListener('message', this.adPostmessageListener);
  }

  ngOnDestroy(): void {
    this.clearRotationTimeout();
    window.removeEventListener('message', this.adPostmessageListener);
    if (this.removeAdClickListener) {
      this.removeAdClickListener();
      this.removeAdClickListener = null;
    }
    if (this.removeVisibilityChangeListener) {
      this.removeVisibilityChangeListener();
      this.removeVisibilityChangeListener = null;
    }
  }

  ngOnInit(): void {
    let thizz = this;

    this.removeVisibilityChangeListener = this.renderer.listen(
      document,
      'visibilitychange',
      () => this.onVisibilityChange()
    );
    this.removeAdClickListener = this.renderer.listen(
      this.adContainer.nativeElement,
      'click',
      () => this.adClickedEvent()
    );

    this.loadAdData(this.isOnlyShowingDefaultAds).pipe(first()).subscribe((res: any) => {
      const invocationCode = res && res.hubAds && res.hubAds.length
        ? res.hubAds[0].invocation_code
        : null;

      this.renderReviveAd(invocationCode);
    });
  }

  loadAdData(showDefaultAds: boolean): Observable<any> {
    const headers = new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
    });
    let url = environment.COMMONS_API_ENDPOINT + '/hub/advertisement/' + this.translateService.currentLang + '/';
    if (showDefaultAds) {
      url += 'DEFAULT_';
    }
    url += this.adType;
    if (environment.mockup) {
        const mockAdFiles = {
          TOP: 'assets/test/ad.json',
          SIDE: 'assets/test/ad-side.json',
          MOBILE_TOP: 'assets/test/ad-mobile-top.json',
          MOBILE_MIDDLE: 'assets/test/ad-mobile-middle.json'
        };
        const mockAdFile = mockAdFiles[this.adType] || mockAdFiles.TOP;
        return this.httpClient.get(mockAdFile, { responseType: 'json' }); //Demo
    } else {
        return this.httpClient.post(url, { responseType: 'json', headers: headers });
    }
  }

  adClickedEvent(): void {
    if (!this.currentAdData) {
      return;
    }

    this.datalayerService.advertisementClickedEvent(this.currentAdData);
  }

  public receiveAdViewMessage(thizz: any, event: any) {
    if (event.data.type === 'adViewEvent' && event.data.htmlContent) {
      if (document.hidden) {
        thizz.currentAdData = event.data.htmlContent;
        thizz.currentAdData.language = thizz.currentLanguage;
        thizz.lastShownAdCampaign = event.data.htmlContent.campaign;
        return;
      }

      const adTypes = AppConstants.AD_TYPE_MAPPING[event.data.htmlContent.type];
      if (adTypes && adTypes.indexOf(thizz.adType) > -1 && thizz.lastShownAdCampaign != event.data.htmlContent.campaign && !this.datalayerService.getResizing()) {
        thizz.currentAdData = event.data.htmlContent;
        thizz.currentAdData.language = thizz.currentLanguage;
        thizz.datalayerService.advertisementViewedEvent(thizz.currentAdData);
        thizz.lastShownAdCampaign = event.data.htmlContent.campaign;
      }
    }
  }

  // Parses and dynamically renders the Revive asynchronous invocation code.
  private renderReviveAd(invocationCode: string): void {
    if (!this.adContainer) {
      return;
    }

    this.clearRotationTimeout();
    this.reviveId = null;
    this.adContainer.nativeElement.innerHTML = '';

    if (!invocationCode) {
      return;
    }

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = invocationCode;

    const insElement = tempDiv.querySelector('ins');
    const scriptElement = tempDiv.querySelector('script');

    if (!insElement || !scriptElement || !scriptElement.getAttribute('src')) {
      return;
    }

    this.reviveId = insElement.getAttribute('data-revive-id');

    const revivePlaceholder = this.renderer.createElement('ins');
    Array.from(insElement.attributes).forEach(attribute => {
      this.renderer.setAttribute(revivePlaceholder, attribute.name, attribute.value);
    });

    const reviveScript = this.renderer.createElement('script');
    Array.from(scriptElement.attributes).forEach(attribute => {
      this.renderer.setAttribute(reviveScript, attribute.name, attribute.value);
    });

    this.renderer.appendChild(this.adContainer.nativeElement, revivePlaceholder);
    this.renderer.appendChild(this.adContainer.nativeElement, reviveScript);

    if (this.reviveId) {
      this.refreshReviveAd(false);
      this.scheduleNextRotation();
    }
  }

  // Schedules the next ad refresh while the page is visible.
  private scheduleNextRotation(): void {
    this.clearRotationTimeout();

    if (!this.reviveId || document.hidden) {
      return;
    }

    this.rotationTimeout = setTimeout(() => {
      if (!document.hidden) {
        this.rotateReviveAd();
      }
      this.scheduleNextRotation();
    }, this.AD_ROTATION_INTERVAL);
  }

  // Triggers a scheduled refresh of the current Revive ad zone.
  private rotateReviveAd(): void {
    this.refreshReviveAd(true);
  }

  // Safely refreshes the current Revive ad zone, resetting its loaded state when rotating.
  private refreshReviveAd(isRotation: boolean): void {
    const reviveId = this.reviveId;

    if (!reviveId) {
      return;
    }

    const reviveAsync = (window as any).reviveAsync;
    const reviveInstance = reviveAsync && reviveAsync[reviveId];
    const placeholder = this.adContainer
      ? this.adContainer.nativeElement.querySelector('ins[data-revive-id]')
      : null;

    if (!placeholder
      || placeholder.getAttribute('data-revive-id') !== this.reviveId
      || !reviveInstance
      || typeof reviveInstance.refresh !== 'function') {
      return;
    }

    if (isRotation) {
      placeholder.removeAttribute('data-revive-loaded');
    }

    try {
      reviveInstance.refresh();
    } catch (error) {
      // Ignore transient Revive errors. The next scheduled rotation will retry.
    }
  }

  // Pauses ad rotation when the page is hidden and restarts it when the page becomes visible.
  private onVisibilityChange(): void {
    if (document.hidden) {
      this.clearRotationTimeout();
      return;
    }

    this.scheduleNextRotation();
  }

  // Cancels the pending ad rotation timeout.
  private clearRotationTimeout(): void {
    if (this.rotationTimeout) {
      clearTimeout(this.rotationTimeout);
      this.rotationTimeout = null;
    }
  }
}
